import React, { useState, useEffect } from "react";
import { useParams} from "react-router-dom";
import { useManualQuery } from "graphql-hooks";
import dayjs from "dayjs";
import { isValidUUIDv4 } from "../hooks/uuid";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  TablePagination,
  TableSortLabel,
  Rating,
  Tooltip,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import {
  ArrowDropUp,
  ArrowDropDown,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import AlertPopup from "../components/AlertPopup";
import PublicPageLayout from "./PublicPageLayout";
import FloatingMenu from "../components/FloatingMenu";
import EmptyState from "../components/EmptyState";

import placeholderImage from "../assets/r3play-logo-sml.png";

import { ORDERS_BY_TOY_USER_UUID } from "../graphQL/order_qry";
import { USER_DATA_BY_UUID } from "../graphQL/user_qry";

import useIsTablet from "../hooks/useIsTablet";
import useIsMobile from "../hooks/useIsMobile";

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

const PublicRatings = () => {
  const { userUuid } = useParams();
  const isTablet = useIsTablet(1360);
  const isMobile = useIsMobile(833);
  const [mobileSortBy, setMobileSortBy] = useState("purchasedDate");
  const [sortPanelOpen, setSortPanelOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("purchasedDate");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [ratings, setRatings] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [userRatingNo, setUserRatingNo] = useState(4.5);
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);  
  const [warningPopup, setWarningPopup] = useState({
    open: false,    
  });
  const [getRatingsData] = useManualQuery(
    ORDERS_BY_TOY_USER_UUID, {
    fetchPolicy: "network-only",
  });

  const [getUsrData] = useManualQuery(USER_DATA_BY_UUID);

  useEffect(() => {
    if (isMobile && rowsPerPage === 10) setRowsPerPage(5);
    else if (!isMobile && rowsPerPage === 5) setRowsPerPage(10);
  }, [rowsPerPage, isMobile, isTablet]);

  // Handle click outside to close sort panel in Mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      const sortPanel = document.querySelector("[data-sort-panel]");
      if (sortPanel && !sortPanel.contains(event.target) && sortPanelOpen) {
        setSortPanelOpen(false);
      }
    };

    if (sortPanelOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sortPanelOpen]);

  // Set floating menu place based on screen size
  useEffect(() => {
    const handleResize = () => {      
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);
  }, []);  

  const setUserData = async () => {
    if (!userUuid) return;
    if (isValidUUIDv4(userUuid)){  
        const {
          loading: loadingUsrData,
          error: errorCurrUsrData,
          data: usrData,
        } = await getUsrData({
          variables: {
            uuid: userUuid,
          },
        });
        if (!loadingUsrData && usrData) {
          setUserRatingNo(
            Number(usrData.userDataByUuid.ratingNo) || 0
          )
        } else {
          console.log("errorCurrUsrData: ", errorCurrUsrData);
        }
    } else {
      setWarningPopup({
        open: true,
        title: "This URL is not valid.",
      });
    }
  };

 useEffect(() => {
    (async () => setUserData())();
  }, [userUuid]);
  
  const setRatingList = async () => {    
    if (!userUuid) return;
    const {
      loading: loadingRatingListData,
      error: errorRatingListData,
      data: ratingListData,
    } = await getRatingsData({
      variables: {
        userUuid: userUuid,
      },
    });
    
    if (!loadingRatingListData && ratingListData && ratingListData.ordersByToyUseruuid) {              
      setRatings(
        ratingListData.ordersByToyUseruuid.map(
          order => ({
          id: order.orderId,
          uuid: order.uuid,
          photo: order.toyId.toyImages[0]?.fileName || placeholderImage,
          title: order.toyId.title,
          ratingNo: order.toyId.ratingNo,
          ratingComment: order.toyId.ratingComment,
          purchasedBy: order.userId.username,
          purchasedByUuid: order.userId.userUuid,
          purchasedDate: order.orderDate ? dayjs(order.orderDate).format("MMM D, YYYY") : ""
        }))        
      ); 
      setIsLoaded(true);         
    } else {
      console.log("errorRatingListData: ", errorRatingListData);           
    };
  };

  useEffect(() => {
    (async () => setRatingList())();
  }, [userUuid]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  // Sorting handler
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setMobileSortBy(property);
  };

  // Sort data
  const getSortedData = () => {
    let sortedData = [...ratings];

    if (orderBy) {
      sortedData.sort((a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];

        if (orderBy === "title") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (orderBy === "purchasedDate") {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        if (orderBy === "purchasedBy") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (orderBy === "ratingNo") {
          aValue = parseFloat(aValue);
          bValue = parseFloat(bValue);
        }

        if (aValue < bValue) {
          return order === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return order === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedData;
  };

  const sortedData = getSortedData();
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <PublicPageLayout 
      activeMenuItem="ratings"
      userUuid={userUuid}
    >
      {/* Mobile View - Only shows on screens < 834px */}
      <Box
        sx={{
          display: "none",
          "@media (max-width: 833px)": {
            display: "block",
          },
        }}
      >
        {/* Mobile Header */}
        <Box p={2}>
          <Typography variant="h6">Ratings</Typography>
          <Typography variant="body2" color="text.secondary">
            View ratings and feedback from buyers
          </Typography>
        </Box>

        {/* Mobile Overall Rating */}
        <Box px={2} py={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              Overall Rating
            </Typography>
            <Box
              sx={{
                width: "1px",
                height: "20px",
                backgroundColor: "#e0e0e0",
                opacity: 0.6,
              }}
            />
            <StyledTooltip title={`${userRatingNo} of 5`}>
              <Box sx={{ display: "inline-block" }}>
                <Rating
                  value={userRatingNo}
                  precision={0.5}
                  readOnly
                  size="small"
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    "& .MuiRating-iconFilled": {
                      color: "#E53935",
                    },
                    "& .MuiRating-iconEmpty": {
                      color: "#e0e0e0",
                    },
                  }}
                />
              </Box>
            </StyledTooltip>
          </Box>

          {/* Number of Ratings */}
          <Box
            pt={1}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              Total No of Ratings: {ratings.length}
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {!isLoaded ? (
          <Box p={4} textAlign="center">Loading...</Box>
        ) : isLoaded && ratings.length === 0 ? (
          <EmptyState 
            type="ratings"
            showIcon={false}
            title="No Ratings Available"
            description="This user hasn't received any ratings yet."
          />
        ) : (
          <>
            {/* Mobile Sort Panel */}
            <Box pr={2} pl={2}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  bgcolor: "#F5F5F5", // Gray box around
                  p: "16px",
                  borderRadius: 2,
                  "@media (max-width: 833px)": {
                    flexDirection: "column",
                    alignItems: "stretch",
                  },
                }}
              >
                <Box sx={{ position: "relative", width: "100%" }} data-sort-panel>
                  <TextField
                    label="Sort by"
                    value={
                      mobileSortBy
                        ? (() => {
                            const options = {
                              title: "Title",
                              ratingNo: "Rating",
                              purchasedBy: "Purchased By",
                              purchasedDate: "Purchased Date",
                            };
                            return options[mobileSortBy] || "";
                          })()
                        : ""
                    }
                    onClick={() => setSortPanelOpen(!sortPanelOpen)}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                          {mobileSortBy &&
                            (order === "desc" ? (
                              <ArrowDownward
                                sx={{ fontSize: 20, color: "#666" }}
                              />
                            ) : (
                              <ArrowUpward sx={{ fontSize: 20, color: "#666" }} />
                            ))}
                          {sortPanelOpen ? (
                            <ArrowDropUp sx={{ color: "#666" }} />
                          ) : (
                            <ArrowDropDown sx={{ color: "#666" }} />
                          )}
                        </Box>
                      ),
                    }}
                    sx={{
                      width: "100%",
                      cursor: "pointer",
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        backgroundColor: "white",
                        cursor: "pointer",
                        paddingRight: "8px",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d0d0d0",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e0e0e0",
                          borderWidth: "1px",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#666",
                        fontSize: "14px",
                        "&.Mui-focused": {
                          color: "#666",
                        },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                        pr: 0,
                      },
                      "& .MuiOutlinedInput-input": {
                        cursor: "pointer",
                      },
                    }}
                  />

                  {/* Sort options panel */}
                  {sortPanelOpen && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "40px",
                        left: 0,
                        right: 0,
                        backgroundColor: "white",
                        border: "1px solid #e0e0e0",
                        borderTop: "none",
                        borderRadius: "0 0 4px 4px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        zIndex: 1000,
                      }}
                    >
                      <RadioGroup
                        value={mobileSortBy}
                        onChange={(e) => {
                          setMobileSortBy(e.target.value);
                          setOrder("desc");
                          setOrderBy(e.target.value);
                          setPage(0);
                        }}
                      >
                        {[
                          { value: "title", label: "Title" },
                          { value: "ratingNo", label: "Rating" },
                          { value: "purchasedBy", label: "Purchased By" },
                          { value: "purchasedDate", label: "Purchased Date" },
                        ].map((option) => (
                          <Box
                            key={option.value}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              px: 1,
                              pl: 0,
                              py: 0.5,
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                              },
                            }}
                          >
                            <FormControlLabel
                              value={option.value}
                              control={
                                <Radio
                                  size="small"
                                  sx={{
                                    color: "#666",
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{ fontSize: "14px", color: "#333" }}
                                >
                                  {option.label}
                                </Typography>
                              }
                              sx={{ margin: 0, flex: 1 }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                width: "55px",
                                justifyContent: "space-between",
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMobileSortBy(option.value);
                                  setOrder("desc");
                                  setOrderBy(option.value);
                                  setPage(0);
                                  setSortPanelOpen(false);
                                }}
                                sx={{
                                  p: 0.25,
                                  backgroundColor:
                                    mobileSortBy === option.value &&
                                    order === "desc"
                                      ? "#fce4ec"
                                      : "transparent",
                                }}
                              >
                                <ArrowDownward
                                  fontSize="small"
                                  sx={{ fontSize: 20, color: "#666" }}
                                />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMobileSortBy(option.value);
                                  setOrder("asc");
                                  setOrderBy(option.value);
                                  setPage(0);
                                  setSortPanelOpen(false);
                                }}
                                sx={{
                                  p: 0.25,
                                  backgroundColor:
                                    mobileSortBy === option.value &&
                                    order === "asc"
                                      ? "#fce4ec"
                                      : "transparent",
                                }}
                              >
                                <ArrowUpward
                                  fontSize="small"
                                  sx={{ fontSize: 20, color: "#666" }}
                                />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}
                      </RadioGroup>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {/* Mobile Cards */}
            <Box px={2}>
              {paginatedData.map((rating, index) => (
                <Box
                  key={rating.id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#FDECF0" : "white",
                    borderRadius: "8px",
                    mb: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    overflow: "hidden",
                  }}
                >
                  <Table>
                    <TableBody>
                      {/* Row 1: Image + Title */}
                      <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                        <TableCell sx={{ py: 2, width: "135px", border: "none" }}>
                          <Avatar
                            src={rating.photo}
                            alt={rating.title}
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: "8px",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 2, border: "none" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              fontWeight: "500",
                              lineHeight: 1.4,
                            }}
                          >
                            {rating.title}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Row 2: Rating + Feedback */}
                      <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                        <TableCell sx={{ py: 2, border: "none" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#666",
                              fontWeight: "bold",
                              mb: 0.5,
                            }}
                          >
                            Buyer's Rating
                          </Typography>
                          <StyledTooltip title={`${rating.ratingNo} of 5`}>
                            <Box sx={{ display: "inline-block" }}>
                              <Rating
                                value={rating.ratingNo}
                                precision={0.5}
                                readOnly
                                size="small"
                                sx={{
                                  "& .MuiRating-iconFilled": {
                                    color: "#E53935",
                                  },
                                }}
                              />
                            </Box>
                          </StyledTooltip>
                        </TableCell>
                        <TableCell sx={{ py: 2, border: "none" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#666",
                              fontWeight: "bold",
                              mb: 0.5,
                            }}
                          >
                            Buyer's Feedback
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                            }}
                          >
                            {rating.ratingComment}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Row 3: Purchased By + Date */}
                      <TableRow>
                        <TableCell sx={{ py: 2, border: "none" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#666",
                              fontWeight: "bold",
                              mb: 0.5,
                            }}
                          >
                            Purchased By
                          </Typography>
                          <Link
                            to={`/user/${rating.purchasedByUuid}/profile`}
                            style={{
                              color: "#E53935",
                              textDecoration: "none",
                              fontSize: "14px",
                            }}
                          >
                            {rating.purchasedBy}
                          </Link>
                        </TableCell>
                        <TableCell sx={{ py: 2, border: "none" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#666",
                              fontWeight: "bold",
                              mb: 0.5,
                            }}
                          >
                            Purchased Date
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                            }}
                          >
                            {rating.purchasedDate}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>

                  {/* Card bottom padding */}
                  <Box sx={{ pb: 2 }} />
                </Box>
              ))}
            </Box>

            {/* Mobile Pagination */}
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <TablePagination
                rowsPerPageOptions={[5, 15, 20]}
                component="div"
                count={sortedData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    paddingLeft: 1,
                    paddingRight: 1,
                    minHeight: "52px",
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    fontSize: "14px",
                    color: "#666",
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: "14px",
                  },
                  "& .MuiTablePagination-actions": {
                    "& .MuiIconButton-root": {
                      padding: "6px",
                    },
                  },
                }}
              />
            </Box>
          </>
        )}
      </Box>
      {/* Desktop/Tablet View - Only shows on screens >= 834px */}
      <Box
        sx={{
          "@media (max-width: 833px)": {
            display: "none",
          },
        }}
      >
        {/* Header Section */}

        <Box
          px={2}
          py={1}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Title Section */}
          <Box>
            <Typography variant="h6">Ratings</Typography>
            <Typography variant="body2" color="text.secondary">
              View ratings and feedback from buyers
            </Typography>
          </Box>

          {/* Overall Rating Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                Overall Rating
              </Typography>
              <Box
                sx={{
                  width: "1px",
                  height: "20px",
                  backgroundColor: "#e0e0e0",
                  opacity: 0.6,
                }}
              />

              <StyledTooltip title={`${userRatingNo} of 5`}>
                <Box sx={{ display: "inline-block" }}>
                  <Rating
                    value={userRatingNo}
                    precision={0.5}
                    readOnly
                    size="small"
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      "& .MuiRating-iconFilled": {
                        color: "#E53935",
                      },
                      "& .MuiRating-iconEmpty": {
                        color: "#e0e0e0",
                      },
                    }}
                  />
                </Box>
              </StyledTooltip>
            </Box>

            {/* Number of Ratings */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                Total No of Ratings: {ratings.length}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        {!isLoaded ? (
          <Box p={4} textAlign="center">Loading...</Box>
        ) : isLoaded && ratings.length === 0 ? (
          <EmptyState 
            type="ratings"
            showIcon={false}
            title="No Ratings Available"
            description="This user hasn't received any ratings yet."
          />
        ) : (
          <>  
            {/* Table */}
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        width: "70px",
                        color: "#333",
                        py: 2,
                      }}
                    >
                      Photo
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        width: "170px",
                        color: "#333",
                        py: 2,
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "title"}
                        direction={orderBy === "title" ? order : "asc"}
                        onClick={() => handleRequestSort("title")}
                        sx={{
                          fontWeight: "600",
                          color: "#333",
                          "&.Mui-active": {
                            color: "#333",
                          },
                          "&:hover": {
                            color: "#333",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#333 !important",
                          },
                        }}
                      >
                        Title/Summary
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        width: "160px",
                        color: "#333",
                        py: 2,
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "ratingNo"}
                        direction={orderBy === "ratingNo" ? order : "asc"}
                        onClick={() => handleRequestSort("ratingNo")}
                        sx={{
                          fontWeight: "600",
                          color: "#333",
                          "&.Mui-active": {
                            color: "#333",
                          },
                          "&:hover": {
                            color: "#333",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#333 !important",
                          },
                        }}
                      >
                        Buyer's Rating
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        width: "175px",
                        color: "#333",
                        py: 2,
                      }}
                    >
                      Buyer's Feedback
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        width: "110px",
                        color: "#333",
                        py: 2,
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "purchasedBy"}
                        direction={orderBy === "purchasedBy" ? order : "asc"}
                        onClick={() => handleRequestSort("purchasedBy")}
                        sx={{
                          fontWeight: "600",
                          color: "#333",
                          "&.Mui-active": {
                            color: "#333",
                          },
                          "&:hover": {
                            color: "#333",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#333 !important",
                          },
                        }}
                      >
                        Purchased By
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        width: "170px",
                        color: "#333",
                        py: 2,
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "purchasedDate"}
                        direction={orderBy === "purchasedDate" ? order : "asc"}
                        onClick={() => handleRequestSort("purchasedDate")}
                        sx={{
                          fontWeight: "600",
                          color: "#333",
                          "&.Mui-active": {
                            color: "#333",
                          },
                          "&:hover": {
                            color: "#333",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#333 !important",
                          },
                        }}
                      >
                        Purchased Date
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((rating) => (
                    <React.Fragment key={rating.id}>
                      <TableRow
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f9f9f9",
                          },
                          ...(isTablet && {
                            "& td": {
                              borderBottom: "none",
                            },
                          }),
                        }}
                      >
                        <TableCell sx={{ py: 2, pr: 0, pb: isTablet ? 0 : 2 }}>
                          <Avatar
                            src={rating.photo}
                            alt={rating.title}
                            sx={{
                              width: isTablet ? 40 : 80,
                              height: isTablet ? 40 : 80,
                              borderRadius: "8px",
                            }}
                          />
                        </TableCell>

                        {/* Title Cell - Spans 5 columns on tablet, normal on desktop */}
                        <TableCell
                          colSpan={isTablet ? 5 : 1}
                          sx={{
                            py: isTablet ? 1 : 2,
                            maxWidth: "300px",
                            "@media (max-width: 1360px)": {
                              maxWidth: "none",
                              paddingBottom: 0,
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              fontWeight: "500",
                              lineHeight: 1.4,
                            }}
                          >
                            {rating.title}
                          </Typography>
                        </TableCell>

                        {/* Desktop columns - hidden on tablet */}
                        {!isTablet && (
                          <>
                            <TableCell sx={{ py: 2 }}>
                              <StyledTooltip title={`${rating.ratingNo} of 5`}>
                                <Box sx={{ display: "inline-block" }}>
                                  <Rating
                                    value={rating.ratingNo}
                                    precision={0.5}
                                    readOnly
                                    size="small"
                                    sx={{
                                      "& .MuiRating-iconFilled": {
                                        color: "#E53935",
                                      },
                                    }}
                                  />
                                </Box>
                              </StyledTooltip>
                            </TableCell>
                            <TableCell sx={{ py: 2, maxWidth: "200px" }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#333",
                                  lineHeight: 1.4,
                                }}
                              >
                                {rating.ratingComment}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Link
                                to={`/user/${rating.purchasedByUuid}/profile`}
                                style={{
                                  color: "#E53935",
                                  textDecoration: "none",
                                  fontSize: "14px",
                                }}
                              >
                                {rating.purchasedBy}
                              </Link>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#333",
                                }}
                              >
                                {rating.purchasedDate}
                              </Typography>
                            </TableCell>
                          </>
                        )}
                      </TableRow>

                      {/* Second row for tablet view */}
                      {isTablet && (
                        <TableRow
                          key={`${rating.id}-details`}
                          sx={{
                            "&:hover": {
                              backgroundColor: "#f9f9f9",
                            },
                            "& td": {
                              borderTop: "none",
                            },
                          }}
                        >
                          {/* Empty cell for Photo and Title column */}
                          <TableCell sx={{ py: 0 }}></TableCell>
                          <TableCell sx={{ py: 0 }}></TableCell>

                          <TableCell sx={{ pt: 0, pb: 1 }}>
                            <StyledTooltip title={`${rating.ratingNo} of 5`}>
                              <Box sx={{ display: "inline-block" }}>
                                <Rating
                                  value={rating.ratingNo}
                                  precision={0.5}
                                  readOnly
                                  size="small"
                                  sx={{
                                    "& .MuiRating-iconFilled": {
                                      color: "#E53935",
                                    },
                                  }}
                                />
                              </Box>
                            </StyledTooltip>
                          </TableCell>
                          <TableCell sx={{ pt: 0, pb: 1, maxWidth: "200px" }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                                lineHeight: 1.4,
                              }}
                            >
                              {rating.ratingComment}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ pt: 0, pb: 1 }}>
                            <Link
                              to={`/user/${rating.purchasedByUuid}/profile`}
                              style={{
                                color: "#E53935",
                                textDecoration: "none",
                                fontSize: "14px",
                              }}
                            >
                              {rating.purchasedBy}
                            </Link>
                          </TableCell>
                          <TableCell sx={{ pt: 0, pb: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                              }}
                            >
                              {rating.purchasedDate}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 15, 20]}
                component="div"
                count={sortedData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: "1px solid #e0e0e0",
                  "& .MuiTablePagination-toolbar": {
                    paddingLeft: 2,
                    paddingRight: 2,
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    fontSize: "14px",
                    color: "#666",
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: "14px",
                  },
                }}
              />
            </TableContainer>
          </>
        )}
      </Box>
      
      <AlertPopup
          open={warningPopup.open}
          title={warningPopup.title}
          message=""
          confirmText="OK"
          cancelText= ""
          onConfirm={() => setWarningPopup(false)}
          onClose={() => setWarningPopup(false)}
        />
        
      <FloatingMenu left={floatMenuLeftMargin} />
    </PublicPageLayout>
  );
};

export default PublicRatings;
